import React, { useState } from "react";
import { Button, Paper, Typography } from "@mui/material";
import { t } from "i18next";
import "./IntermissionPanel.css";

export interface IntermissionPayload {
  id: number;
  title: string;
  body: string;
  buttonLabel: string;
  imageUrl: string | null;
}

export interface IntermissionPanelProps {
  payload: IntermissionPayload;
  /** Player ids that have already pressed the button. */
  acknowledged: string[];
  totalPlayers: number;
  hasAcknowledged: boolean;
  isHost: boolean;
  onAcknowledge: () => void;
  /** Host-only escape hatch when someone wandered off mid-message. */
  onSkip: () => void;
  mobile?: boolean;
}

/**
 * An admin message pushed into a running game between two turns. The button is a ready gate —
 * play does not resume until every connected player has pressed it — so this is also the one
 * place the whole lobby is guaranteed to be looking at the same screen.
 */
export default function IntermissionPanel({
  payload,
  acknowledged,
  totalPlayers,
  hasAcknowledged,
  isHost,
  onAcknowledge,
  onSkip,
  mobile,
}: IntermissionPanelProps) {
  // A dead image link should collapse to a text-only message, not a broken-image icon.
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = !!payload.imageUrl && !imageFailed;

  return (
    <div className={"itm-root" + (mobile ? " itm-root--mobile" : "")}>
      <Paper className="itm-card" elevation={3}>
        <div className="itm-kicker">{t("intermission_kicker")}</div>
        <Typography className="itm-title" variant="h5" align="center">
          {payload.title}
        </Typography>

        {payload.body && <p className="itm-body">{payload.body}</p>}

        {showImage && (
          <div className="itm-media">
            <img
              src={payload.imageUrl!}
              alt=""
              onError={() => setImageFailed(true)}
              loading="eager"
            />
          </div>
        )}

        <Button
          variant={hasAcknowledged ? "outlined" : "contained"}
          size="large"
          className="itm-button"
          onClick={onAcknowledge}
          disabled={hasAcknowledged}
        >
          {hasAcknowledged
            ? `${t("intermission_waiting")} (${acknowledged.length}/${totalPlayers})`
            : `${payload.buttonLabel} (${acknowledged.length}/${totalPlayers})`}
        </Button>

        {isHost && hasAcknowledged && acknowledged.length < totalPlayers && (
          <button type="button" className="itm-skip" onClick={onSkip}>
            {t("intermission_skip")}
          </button>
        )}
      </Paper>
    </div>
  );
}
